/**
 * General study-destination guidance for an estimated IELTS band.
 *
 * What this module holds is the *published minimum English requirement* ranges
 * that institutions and courses commonly advertise, grouped into bands. It is
 * not an admission prediction and it deliberately names no institution:
 * personalised, institution-level matching is the registered-user feature
 * (IELTS-277), and a public page that named universities against a practice
 * score would be read as an offer of a place at them.
 *
 * Every string below is hedged on purpose ("usually", "commonly", "most").
 * Someone can make a real decision about their education on this page, so the
 * copy has to stay a guidance range rather than a promise.
 */

import { isReportableIeltsBand } from '@/domain/scoring/cefr'

export type StudyDestinationTierId =
  | 'below_common_minimums'
  | 'foundation_pathway'
  | 'undergraduate'
  | 'postgraduate_competitive'
  | 'most_selective'

export interface StudyDestinationTier {
  id: StudyDestinationTierId
  /** Inclusive lower and upper band bounds. Together the tiers cover 0.0–9.0. */
  min: number
  max: number
  bandLabel: string
  headline: string
  summary: string
  /** Course *types*, never a named institution — see the module comment. */
  commonlyOpens: readonly string[]
  destinationIds: readonly StudyDestinationId[]
  nextStep: string
}

export type StudyDestinationId = 'uk' | 'canada' | 'australia' | 'ireland' | 'new_zealand' | 'usa'

export interface StudyDestination {
  id: StudyDestinationId
  flag: string
  name: string
}

/**
 * Countries where IELTS is widely used for study applications. A country
 * appearing here is an invitation to explore its courses, not a claim that a
 * band satisfies admission or visa rules there.
 */
export const STUDY_DESTINATIONS: readonly StudyDestination[] = [
  { id: 'uk', flag: '🇬🇧', name: 'United Kingdom' },
  { id: 'canada', flag: '🇨🇦', name: 'Canada' },
  { id: 'australia', flag: '🇦🇺', name: 'Australia' },
  { id: 'ireland', flag: '🇮🇪', name: 'Ireland' },
  { id: 'new_zealand', flag: '🇳🇿', name: 'New Zealand' },
  { id: 'usa', flag: '🇺🇸', name: 'United States' },
] as const

const PATHWAY_DESTINATIONS: readonly StudyDestinationId[] = [
  'uk',
  'canada',
  'australia',
  'ireland',
  'new_zealand',
]

const DEGREE_DESTINATIONS: readonly StudyDestinationId[] = [
  ...PATHWAY_DESTINATIONS,
  'usa',
]

/**
 * The four thresholds come from the minimum English requirements institutions
 * publish most widely: ~6.0 for foundation and pathway entry, 6.5 for the bulk
 * of undergraduate study in the UK, Ireland, Australia and New Zealand, 7.0 for
 * postgraduate and competitive courses, and 7.5+ for the most selective.
 *
 * Everything under 6.0 is one tier rather than several. The brief gives no
 * thresholds below 6.0, and the honest answer across that whole range is the
 * same one — pre-sessional English first — so splitting it would invent
 * precision the published requirements do not support.
 */
export const STUDY_DESTINATION_TIERS: readonly StudyDestinationTier[] = [
  {
    id: 'below_common_minimums',
    min: 0,
    max: 5.5,
    bandLabel: 'Below band 6.0',
    headline: 'Most degree courses ask for more than this, for now.',
    summary:
      'Published English requirements for degree-level study usually start at around band 6.0. A band below that does not close the door: institutions in every major study destination run pre-sessional and general English programmes that lead onto a degree once your English reaches the level the course asks for.',
    commonlyOpens: [
      'General English and pre-sessional English language programmes',
      'Pre-sessional courses that lead onto a degree at the same institution',
      'Some college, vocational and short courses, which often publish lower requirements than degrees',
    ],
    destinationIds: PATHWAY_DESTINATIONS,
    nextStep:
      'Work on the section holding your average down, then take another practice test. The overall band is an average of four skills, so lifting your weakest one moves it fastest.',
  },
  {
    id: 'foundation_pathway',
    min: 6,
    max: 6,
    bandLabel: 'Band 6.0',
    headline: 'Foundation and pathway routes usually open here.',
    summary:
      'Band 6.0 is around the level at which published minimums start. It commonly meets the requirement for foundation years, international-year-one and other pathway programmes that feed into a degree, and for undergraduate courses at institutions that set 6.0 as their floor.',
    commonlyOpens: [
      'Foundation year and international pathway programmes',
      'Undergraduate courses at institutions that publish a 6.0 minimum',
      'Many college and diploma-level courses',
    ],
    destinationIds: PATHWAY_DESTINATIONS,
    nextStep:
      'Half a band more opens the mainstream undergraduate range, so another practice test is usually worth it before you shortlist courses.',
  },
  {
    id: 'undergraduate',
    min: 6.5,
    max: 6.5,
    bandLabel: 'Band 6.5',
    headline: 'The common undergraduate range in the UK, Ireland, Australia and beyond.',
    summary:
      'Band 6.5 is the most widely published undergraduate minimum across UK, Irish, Australian and New Zealand institutions, and a common requirement in Canada and the United States too. A good number of taught postgraduate courses also start at 6.5.',
    commonlyOpens: [
      'Most undergraduate degrees in the UK, Ireland, Australia and New Zealand',
      'A wide range of undergraduate courses in Canada and the United States',
      'Some taught postgraduate courses',
    ],
    destinationIds: DEGREE_DESTINATIONS,
    nextStep:
      'Check the per-section minimum as well as the overall one: plenty of courses in this range also ask for no single section below 6.0.',
  },
  {
    id: 'postgraduate_competitive',
    min: 7,
    max: 7,
    bandLabel: 'Band 7.0',
    headline: 'Competitive and postgraduate courses come into range.',
    summary:
      'Band 7.0 covers most taught and research postgraduate requirements and the more competitive undergraduate courses. It is also around the level published for many courses in regulated professions, though several of those set higher, subject-specific requirements of their own.',
    commonlyOpens: [
      'Most taught and research postgraduate degrees',
      'Competitive undergraduate courses',
      'Many courses in regulated professions, which usually set per-section minimums as well',
    ],
    destinationIds: DEGREE_DESTINATIONS,
    nextStep:
      'Regulated fields such as healthcare, law and teaching often publish a requirement above the institution minimum, and the professional register can ask for higher again. Read the professional requirement, not only the course one.',
  },
  {
    id: 'most_selective',
    min: 7.5,
    max: 9,
    bandLabel: 'Band 7.5 and above',
    headline: 'In range for the most selective published requirements.',
    summary:
      'Band 7.5 and above meets or exceeds nearly every published English requirement, including those of the most selective courses. At this level English is rarely what decides an application. Academic results, references and your personal statement usually weigh more.',
    commonlyOpens: [
      'The most selective undergraduate and postgraduate courses',
      'Courses that publish an overall minimum of 7.5 or higher',
      'Programmes that also set a high minimum in every individual section',
    ],
    destinationIds: DEGREE_DESTINATIONS,
    nextStep:
      'English is unlikely to be your limiting factor. Put the effort into the academic side of the application, and check whether the courses you want set a per-section minimum you have not hit yet.',
  },
] as const

export interface StudyDestinationCaveat {
  title: string
  detail: string
}

/**
 * Shown with every result, not folded away behind a disclosure. These are the
 * four ways a reader could act on this page and be wrong, so they stay visible.
 */
export const STUDY_DESTINATION_CAVEATS: readonly StudyDestinationCaveat[] = [
  {
    title: 'This is an estimated practice band, not an official IELTS result.',
    detail:
      'Only a test sat at an official IELTS test centre produces a result an institution or a government will accept. Nothing on this page is an IELTS score.',
  },
  {
    title: 'General guidance, never a promise about admission.',
    detail:
      'Every institution and every course sets its own English requirement, and meeting it does not mean an offer. Always check the requirement published for the specific course, in the year you are applying.',
  },
  {
    title: 'Most courses also set a minimum in each section.',
    detail:
      'A qualifying overall band can still fall short if one of the four skills sits below the per-section minimum. Read both numbers on the course page.',
  },
  {
    title: 'Study visas are a separate requirement.',
    detail:
      'Immigration English requirements are set by governments rather than institutions, and some countries require a specific approved version of the test. A practice band is never accepted for a visa.',
  },
] as const

interface GuidedStudyDestinations {
  status: 'guided'
  band: number
  tier: StudyDestinationTier
}

interface UnavailableStudyDestinations {
  status: 'unavailable'
  band: null
  tier: null
  reason: string
}

export type StudyDestinationGuidance = GuidedStudyDestinations | UnavailableStudyDestinations

const UNAVAILABLE_REASON =
  'Study-destination guidance needs a valid overall IELTS band from 0.0 to 9.0, reported in a whole or half band.'

/**
 * Map an estimated overall band onto its published-requirement range.
 *
 * Fails closed on anything that is not a reportable whole or half band. Guessing
 * a tier from a malformed value would put study guidance in front of someone on
 * the strength of a number we could not read.
 */
export function guideStudyDestinations(value: unknown): StudyDestinationGuidance {
  if (!isReportableIeltsBand(value)) {
    return { status: 'unavailable', band: null, tier: null, reason: UNAVAILABLE_REASON }
  }

  const tier = STUDY_DESTINATION_TIERS.find(({ min, max }) => value >= min && value <= max)
  if (!tier) {
    // Unreachable while the tiers stay gap-free, but keeps the function
    // fail-closed rather than rendering an empty guidance card if they drift.
    return { status: 'unavailable', band: null, tier: null, reason: UNAVAILABLE_REASON }
  }

  return { status: 'guided', band: value, tier }
}
